import { Trash2 } from 'lucide-react'
import { Modal, HelpIcon, Button } from '@/components/ui'
import { PrivacySubject } from '@/models/privacy'
import {
  StatusBadge,
  VerificationBadge,
  JurisdictionBadge,
  RequestTypes,
} from './StatusBadge'
import { AuditLogSection } from './AuditLogSection'
import { formatDateTime, formatDate } from '@/lib/utils/date'
import { getDueDateHelp } from '../constants/help-content'

interface PrivacyDetailModalProps {
  subject: PrivacySubject | null
  isOpen: boolean
  onClose: () => void
  onEdit?: (subject: PrivacySubject) => void
  onDelete?: (subject: PrivacySubject) => void
}

export function PrivacyDetailModal({
  subject,
  isOpen,
  onClose,
  onEdit,
  onDelete,
}: PrivacyDetailModalProps) {
  if (!subject) return null

  const detailRow = (label: React.ReactNode, value: React.ReactNode) => {
    const stringValue = typeof value === 'string' ? value : undefined
    return (
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</dt>
        <dd
          className="mt-1 text-sm text-slate-900 dark:text-white sm:mt-0 sm:col-span-2 truncate"
          title={stringValue}
        >
          {value || '-'}
        </dd>
      </div>
    )
  }

  const footer = (
    <div className="flex justify-between">
      <div>
        {onDelete && (
          <Button
            variant="secondary"
            onClick={() => onDelete(subject)}
            className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        )}
      </div>
      <div className="flex gap-3">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {onEdit && (
          <Button variant="primary" onClick={() => onEdit(subject)}>
            Edit
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Privacy Subject Details"
      size="lg"
      footer={footer}
    >
      <div className="divide-y divide-slate-200 dark:divide-slate-700">
        {/* Header */}
        <div className="pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3
                className="text-lg font-medium text-slate-900 dark:text-white truncate"
                title={subject.email || subject.phone || undefined}
              >
                {subject.email || subject.phone || 'No contact'}
              </h3>
              {!subject.email && subject.phone && (
                <p className="text-xs text-slate-500 dark:text-slate-400">Phone-only record</p>
              )}
              {(subject.first_name || subject.last_name) && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {[subject.first_name, subject.last_name].filter(Boolean).join(' ')}
                </p>
              )}
            </div>
            <JurisdictionBadge jurisdiction={subject.jurisdiction} />
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <StatusBadge status={subject.status} />
            <VerificationBadge status={subject.verification_status} />
          </div>
        </div>

        {/* Contact Info */}
        <div className="py-4">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
            Contact Information
          </h4>
          <dl>
            {subject.email ? (
              <>
                {detailRow('Email', subject.email)}
                {detailRow('Normalized Email', subject.email_normalized)}
              </>
            ) : (
              <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">No email on file</div>
            )}
            {detailRow('Phone', subject.phone)}
            {subject.phone_normalized && detailRow('Normalized Phone', subject.phone_normalized)}
          </dl>
        </div>

        {/* Request Types */}
        <div className="py-4">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
            Request Types
          </h4>
          <RequestTypes
            optOutSale={subject.opt_out_sale}
            optOutShare={subject.opt_out_share}
            removeData={subject.remove_data}
            doNotContact={subject.do_not_contact}
          />
          <dl className="mt-3">
            {detailRow('Opt-Out Sale', subject.opt_out_sale ? 'Yes' : 'No')}
            {detailRow('Opt-Out Share', subject.opt_out_share ? 'Yes' : 'No')}
            {detailRow('Remove Data', subject.remove_data ? 'Yes' : 'No')}
            {detailRow('Do Not Contact', subject.do_not_contact ? 'Yes' : 'No')}
          </dl>
        </div>

        {/* Dates */}
        <div className="py-4">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Timeline</h4>
          <dl>
            {detailRow('Received', formatDateTime(subject.received_at))}
            {detailRow(
              <span className="flex items-center gap-1">
                Due Date
                <HelpIcon content={getDueDateHelp(subject.jurisdiction)} size="sm" position="right" />
              </span>,
              formatDate(subject.due_date)
            )}
            {subject.verified_at && detailRow('Verified', formatDateTime(subject.verified_at))}
            {subject.completed_at && detailRow('Completed', formatDateTime(subject.completed_at))}
            {detailRow('Created', formatDateTime(subject.created_at))}
            {detailRow('Updated', formatDateTime(subject.updated_at))}
          </dl>
        </div>

        {/* Source & Notes */}
        {(subject.source || subject.notes) && (
          <div className="py-4">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
              Additional Information
            </h4>
            <dl>
              {subject.source && detailRow('Source', subject.source)}
              {subject.notes && detailRow('Notes', subject.notes)}
            </dl>
          </div>
        )}

        {/* Audit Log */}
        <AuditLogSection subjectId={subject.id} />
      </div>
    </Modal>
  )
}
